// @ts-check
export const CONTENT_SUGGESTIONS_HTML = `
  <div id="closeContentCoachButton">
    <i class="fa-regular fa-xmark"></i>
  </div>
  <div id="contentCoachSpinner"></div>
  <div id="contentCoachSuggestions">
    <div id="totalScoreContainer">
      <span id="emailTotalScoreText"></span>
      <div id="totalScoreProgressCircle">
        <svg id="circle" viewBox="0 0 114 114">
          <circle class="bg" cx="57" cy="57" r="52" />
          <circle class="meter" cx="57" cy="57" r="52" />
        </svg>
        <span id="emailTotalScoreNum"></span>
      </div>
    </div>
    <hr>
    <span>
      📖&nbsp;&nbsp;<strong>Your content has a reading time of <span id="readingTimeString"></span>.</strong>
      We recommend a reading time of 20 seconds or less to increase response rates.
    </span>
    <hr>
    <span>
      🎓&nbsp;&nbsp;<strong
        >Your content has <span id="readingGradeLevelString"></span> grade reading level.</strong
      >
      Typically, targeting an 8th grade reading level boosts response rates. However, the ideal level may vary greatly based on
      your audience.
    </span>
    <hr>
    <div id="suggestionsList"></div>
  </div>`;

const generateResultsList = (results) => {
  const generateHTMLForSingleResult = (filteredResult) => {
    const { severity, highlightCategory = "", text, title } = filteredResult;
    const highlightClass = highlightCategory ? "highlight" : "";

    return `<span class="suggestion ${severity} ${highlightClass}" data-toggle="tooltip" title="${text}" data-highlight-category="${highlightCategory}">${title}</span>`;
  };

  const generateHTMLForResults = (filteredResults) =>
    filteredResults.map(generateHTMLForSingleResult).join("");

  const needsWorkResults = results.filter(
    (result) => result.severity === "warning" || result.severity === "danger",
  );
  const goodToGoResults = results.filter(
    (result) => result.severity === "success",
  );

  const needsWorkHTML = needsWorkResults.length
    ? `<div id="suggestionsSubGroup"><span class="suggestionsSubTitle">🚫&nbsp;&nbsp;Needs Work</span>${generateHTMLForResults(
        needsWorkResults,
      )}</div><hr>`
    : "";

  const goodToGoHTML = goodToGoResults.length
    ? `<div id="suggestionsSubGroup"><span class="suggestionsSubTitle">✅&nbsp;&nbsp;Ready</span>${generateHTMLForResults(
        goodToGoResults,
      )}</div>`
    : "";

  return `<div id='suggestionsContainer'>${needsWorkHTML}${goodToGoHTML}</div>`;
};

let timeout;
let previousTotalScore = 0;
let previousScoreSelector = "";
let suggestions = {};
export const getSuggestions = () => JSON.parse(JSON.stringify(suggestions));

/**
 * @param {Object} options
 * @param {string} options.content - Html string
 * @param {string} options.contentText - Non-html string
 * @param {string} options.subject - Email subject line
 * @param {"email" | "extension"} options.outreachType
 * @param {boolean} options.isFollowUp
 * @param {boolean} options.isSendAsReply
 * @param {string} options.scoreSelector - JQuery selector for score element to
 *   update with results.
 * @param {boolean} options.contentCoachVisible
 * @param {function(): void} [options.notEnoughWordsCallback] - Executed when
 *   wordCount < 10
 * @param {function(): void} [options.enoughWordsCallback] - Executed when
 *   wordCount < 10
 * @param {function(): void} [options.callback] - After content suggestions
 *   updated (wordCount >= 10 and request complete).
 * @param {function(any): void} options.post
 */
export const updateSuggestions = (options) => {
  suggestions = {}; // reset suggestions

  const {
    content,
    contentText,
    subject,
    outreachType,
    isFollowUp,
    isSendAsReply,
    scoreSelector,
    contentCoachVisible,
    notEnoughWordsCallback,
    enoughWordsCallback,
    callback,
    post,
  } = options;

  clearTimeout(timeout);

  const updateSuggestionsCount = (totalScoreNum) => {
    const isSlideInOpen = $("#content").hasClass("displayContentCoach");
    const openClass = isSlideInOpen ? "opened" : "";
    const disabledClass = !totalScoreNum ? "coach-disabled" : "";
    const title = totalScoreNum
      ? "Represents your total content score. Click to view content coach suggestions."
      : "Keep going! Our content coach works best at 10+ words.";

    const $scoreElement = $(scoreSelector);

    $scoreElement
      .removeClass("opened coach-disabled")
      .addClass(`${openClass} ${disabledClass}`)
      .attr("data-original-title", title)
      .find(".meter")
      .attr("stroke-dashoffset", 100 - totalScoreNum || 0)
      .end()
      .find(".totalScoreNum")
      .html(totalScoreNum || "-");
  };

  const wordCount = contentText.trim().split(/\s+/).length;

  if (wordCount < 10) {
    updateSuggestionsCount(null);
    if (notEnoughWordsCallback) notEnoughWordsCallback();
    return;
  }
  if (enoughWordsCallback) enoughWordsCallback();

  const throwConfetti = () => {
    setTimeout(() => {
      const rect = $("#totalScoreProgressCircle")[0].getBoundingClientRect();
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height / 2;

      // @ts-ignore
      // eslint-disable-next-line no-undef
      confetti({
        particleCount: 75,
        startVelocity: 15,
        spread: 360,
        origin: { x: x / window.innerWidth, y: y / window.innerHeight },
        decay: 0.9,
        gravity: 0.3,
        zIndex: 1051,
      });
    }, 1000);
  };

  timeout = setTimeout(() => {
    post({
      url: "/api/campaigns/content_suggestions",
      data: JSON.stringify({
        content,
        subject,
        outreachType,
        isFollowUp,
        isSendAsReply,
      }),
      success: (res) => {
        const $beforeTooltips = $('[data-toggle="tooltip"]');
        if ("tooltip" in $beforeTooltips) {
          $beforeTooltips.tooltip("dispose");
          $(".tooltip").remove();
        }

        suggestions = res;

        const {
          totalScore,
          readingTimeString,
          readingGradeLevelString,
          results,
        } = suggestions;

        const suggestionsList = generateResultsList(results);

        updateSuggestionsCount(totalScore.num);

        $("#totalScoreContainer")
          .removeClass("text-danger text-warning text-success")
          .addClass(`text-${totalScore.severity.color}`);
        $("#emailTotalScoreText").html(totalScore.severity.text);
        $("#emailTotalScoreNum").html(totalScore.num);
        $("#readingTimeString").html(readingTimeString);
        $("#readingGradeLevelString").html(readingGradeLevelString);
        $("#suggestionsList").html("").html(suggestionsList);

        const setCircleProgress = () => {
          const radius = 52;
          const circumference = 2 * Math.PI * radius;

          const colorMapping = {
            danger: "#e74a3b",
            warning: "#f6c23e",
            success: "#1cc88a",
          };

          const color = colorMapping[totalScore.severity.color];

          $("#totalScoreProgressCircle .meter").css("stroke", color);

          const scorePercentage = totalScore.num / 100;
          const offset = circumference * (1 - scorePercentage);

          $("#totalScoreProgressCircle .meter").css(
            "stroke-dasharray",
            `${circumference} ${circumference}`,
          );

          $("#totalScoreProgressCircle .meter").css(
            "stroke-dashoffset",
            offset.toString(),
          );
        };

        setCircleProgress();

        const scoreChanged = totalScore.num !== previousTotalScore;
        const stepChanged = scoreSelector !== previousScoreSelector;
        const showConfetti =
          totalScore.num === 100 &&
          scoreChanged &&
          !stepChanged &&
          contentCoachVisible;

        if (showConfetti) throwConfetti();

        $("#emailTotalScoreNum").text(totalScore.num);
        $("#emailTotalScoreNum")
          .removeClass()
          .addClass(`text-${totalScore.severity.color}`);

        previousTotalScore = totalScore.num;
        previousScoreSelector = scoreSelector;

        const $afterTooltips = $('[data-toggle="tooltip"]');
        if ("tooltip" in $afterTooltips) {
          $afterTooltips.tooltip();
        }

        if (callback) callback();
      },
    });
  }, 500);
};
